import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { saveWellness } from '../../api';
import { usePlayerData } from './PlayerDataContext';

const today = () => new Date().toISOString().split('T')[0];

const MUSCLE_LOCATIONS = [
  'Full Body','Full Upper Body','Full Lower Body',
  'Neck','Shoulders','Back','Chest','Abdomen',
  'Arms','Groin','Glutes','Quadriceps','Hamstrings','Calves',
];
const PAIN_LOCATIONS = [
  ...MUSCLE_LOCATIONS,
  'Head','Elbow','Wrist','Hand','Finger','Hip','Knee','Ankle','Toe',
];

const STEP_CONFIG = [
  { key: 'sleepDuration', title: 'How long did you sleep?',           subtitle: 'Enter the number of hours',                          type: 'number'     },
  { key: 'sleepQuality',  title: 'What was your sleep efficiency?',   subtitle: 'Open Oura and enter the efficiency percentage',       type: 'efficiency' },
  { key: 'soreness',      title: 'Any muscle soreness?',              subtitle: 'Select all affected areas, or tap Next to skip',     type: 'soreness'   },
  { key: 'stressLevel',   title: 'How stressed are you feeling?',     subtitle: '1 = Completely relaxed  ·  10 = Highly stressed',   type: 'scale', color: '#ef4444' },
  { key: 'pain',          title: 'Any other pain or discomfort?',     subtitle: 'Separate from muscle soreness',                      type: 'pain'       },
];

const getScoreColor = (val, inverse = false) => {
  if (!val) return 'var(--text-muted)';
  const pct = val / 10;
  if (inverse) return pct >= 0.7 ? '#ef4444' : pct >= 0.4 ? '#f59e0b' : '#10b981';
  return pct >= 0.7 ? '#10b981' : pct >= 0.4 ? '#f59e0b' : '#ef4444';
};

function ScaleGrid({ value, onChange, color = '#3b82f6' }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
      {[1,2,3,4,5,6,7,8,9].map(n => (
        <button key={n} onClick={() => onChange(n === value ? null : n)}
          style={{
            height: 68, borderRadius: 14, fontFamily: 'inherit',
            border: `2px solid ${value === n ? color : 'var(--border-strong)'}`,
            background: value === n ? color : 'var(--bg-card)',
            color: value === n ? '#fff' : 'var(--text-secondary)',
            fontSize: 24, fontWeight: 700, cursor: 'pointer',
            transition: 'all 0.15s',
          }}
        >{n}</button>
      ))}
      <button onClick={() => onChange(10 === value ? null : 10)}
        style={{
          height: 68, borderRadius: 14, fontFamily: 'inherit',
          border: `2px solid ${value === 10 ? color : 'var(--border-strong)'}`,
          background: value === 10 ? color : 'var(--bg-card)',
          color: value === 10 ? '#fff' : 'var(--text-secondary)',
          fontSize: 24, fontWeight: 700, cursor: 'pointer',
          gridColumn: '2',
          transition: 'all 0.15s',
        }}
      >10</button>
    </div>
  );
}

function LocationPicker({ options, value, onChange }) {
  const toggle = opt =>
    onChange(value.includes(opt) ? value.filter(v => v !== opt) : [...value, opt]);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {options.map(opt => {
        const sel = value.includes(opt);
        return (
          <button key={opt} onClick={() => toggle(opt)}
            style={{
              padding: '9px 14px', borderRadius: 99,
              border: `1px solid ${sel ? '#3b82f6' : 'var(--border-strong)'}`,
              background: sel ? 'rgba(59,130,246,0.18)' : 'var(--bg-card)',
              color: sel ? '#93c5fd' : 'var(--text-secondary)',
              fontSize: 14, fontWeight: sel ? 600 : 400,
              cursor: 'pointer', fontFamily: 'inherit',
              minHeight: 44,
            }}
          >{opt}</button>
        );
      })}
    </div>
  );
}

export default function MobileWellnessWizard({ playerRecord }) {
  const navigate = useNavigate();
  const { refresh } = usePlayerData();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [sleepHours,       setSleepHours]       = useState('');
  const [sleepMins,        setSleepMins]        = useState('');
  const [sleepQuality,     setSleepQuality]     = useState('');
  const [energyLevel,      setEnergyLevel]      = useState('');
  const [sorenessLocs,     setSorenessLocs]     = useState([]);
  const [musclesoreness,   setMusclesoreness]   = useState(null);
  const [stressLevel,      setStressLevel]      = useState(null);
  const [otherPain,        setOtherPain]        = useState(null);
  const [painLocs,         setPainLocs]         = useState([]);
  const [painLevel,        setPainLevel]        = useState(null);

  const total = STEP_CONFIG.length;
  const cfg   = STEP_CONFIG[step];

  const handleNext = async () => {
    if (step < total - 1) {
      setStep(s => s + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      await handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (!playerRecord) return;
    setSaving(true);
    try {
      await saveWellness({
        player_id:        parseInt(playerRecord.id),
        date:             today(),
        sleep_hours:      (sleepHours !== '' || sleepMins !== '')
          ? Number(sleepHours || 0) + Number(sleepMins || 0) / 60
          : null,
        sleep_quality:    sleepQuality !== '' && sleepQuality !== null ? Number(sleepQuality) : null,
        energy_level:     energyLevel !== '' && energyLevel !== null ? Number(energyLevel) : null,
        muscle_soreness:  musclesoreness || null,
        soreness_location: sorenessLocs,
        stress_level:     stressLevel || null,
        other_pain:       otherPain || false,
        pain_level:       painLevel || null,
        pain_location:    painLocs,
      });
      await refresh();
      setSubmitted(true);
    } catch (err) {
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 20, padding: 32, textAlign: 'center', background: 'var(--bg-app)' }}>
        <CheckCircle size={72} color="#10b981" strokeWidth={1.5} />
        <h2 style={{ fontSize: 24, fontWeight: 700, color: '#10b981', margin: 0 }}>All done!</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: 15, margin: 0 }}>Your wellness check-in has been saved.</p>
        <button onClick={() => navigate('/player/forms')}
          style={{ marginTop: 12, padding: '14px 32px', borderRadius: 12, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
          Done
        </button>
      </div>
    );
  }

  const renderContent = () => {
    switch (cfg.type) {
      case 'number':
        return (
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 10 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <input
                type="number" min={0} max={24} value={sleepHours}
                onChange={e => setSleepHours(e.target.value)}
                placeholder="0"
                style={{
                  width: 110, height: 72, borderRadius: 14, border: '2px solid var(--border-strong)',
                  background: 'var(--bg-card)', color: 'var(--text-primary)',
                  fontSize: 32, fontWeight: 700, textAlign: 'center',
                  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
                }}
              />
              <span style={{ fontSize: 14, color: 'var(--text-muted)', fontWeight: 600 }}>hours</span>
            </div>
            <span style={{ fontSize: 32, color: 'var(--text-muted)', marginBottom: 36, lineHeight: 1 }}>:</span>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <input
                type="number" min={0} max={59} value={sleepMins}
                onChange={e => setSleepMins(e.target.value)}
                placeholder="00"
                style={{
                  width: 110, height: 72, borderRadius: 14, border: '2px solid var(--border-strong)',
                  background: 'var(--bg-card)', color: 'var(--text-primary)',
                  fontSize: 32, fontWeight: 700, textAlign: 'center',
                  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
                }}
              />
              <span style={{ fontSize: 14, color: 'var(--text-muted)', fontWeight: 600 }}>minutes</span>
            </div>
          </div>
        );

      case 'efficiency': {
        const pct = sleepQuality === '' ? 80 : Number(sleepQuality);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* Big editable number */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <input
                type="number" min={0} max={100}
                value={sleepQuality}
                onChange={e => {
                  const raw = e.target.value;
                  if (raw === '') { setSleepQuality(''); return; }
                  setSleepQuality(Math.min(100, Math.max(0, Number(raw))));
                }}
                placeholder="—"
                style={{
                  width: 110, height: 72, borderRadius: 16,
                  border: '2px solid var(--border-strong)',
                  background: 'var(--bg-card)', color: '#8b5cf6',
                  fontSize: 36, fontWeight: 800, textAlign: 'center',
                  outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
                }}
              />
              <span style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-muted)' }}>%</span>
            </div>
            {/* Slider */}
            <div>
              <input
                type="range" min={0} max={100} step={1}
                value={sleepQuality === '' ? 80 : pct}
                onChange={e => setSleepQuality(Number(e.target.value))}
                style={{ width: '100%', accentColor: '#8b5cf6', cursor: 'pointer', height: 8 }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                <span>0% — Poor</span><span>100% — Perfect</span>
              </div>
            </div>
          </div>
        );
      }

      case 'hrv':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="number" min={0} max={300}
                value={energyLevel}
                onChange={e => setEnergyLevel(e.target.value)}
                placeholder="—"
                style={{
                  width: 130, height: 72, borderRadius: 16,
                  border: '2px solid var(--border-strong)',
                  background: 'var(--bg-card)', color: '#10b981',
                  fontSize: 36, fontWeight: 800, textAlign: 'center',
                  outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
                }}
              />
              <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-muted)' }}>ms</span>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', margin: 0 }}>
              Typical range: 20 – 120 ms
            </p>
          </div>
        );

      case 'scale':
        return (
          <>
            <ScaleGrid value={stressLevel} onChange={setStressLevel} color={cfg.color} />
            {stressLevel && (
              <div style={{ textAlign: 'center', marginTop: 20, fontSize: 16, color: cfg.color, fontWeight: 600 }}>
                {stressLevel} / 10
              </div>
            )}
          </>
        );

      case 'soreness':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <LocationPicker options={MUSCLE_LOCATIONS} value={sorenessLocs} onChange={setSorenessLocs} />
            {sorenessLocs.length > 0 && (
              <div>
                <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12 }}>How severe is the soreness?</p>
                <ScaleGrid value={musclesoreness} onChange={setMusclesoreness} color="#f59e0b" />
              </div>
            )}
          </div>
        );

      case 'pain':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[true, false].map(v => (
                <button key={String(v)} onClick={() => setOtherPain(v)}
                  style={{
                    height: 64, borderRadius: 14, fontFamily: 'inherit',
                    border: `2px solid ${otherPain === v ? (v ? '#ef4444' : '#10b981') : 'var(--border-strong)'}`,
                    background: otherPain === v ? (v ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)') : 'var(--bg-card)',
                    color: otherPain === v ? (v ? '#ef4444' : '#10b981') : 'var(--text-secondary)',
                    fontSize: 18, fontWeight: 700, cursor: 'pointer',
                  }}
                >{v ? 'Yes' : 'No'}</button>
              ))}
            </div>
            {otherPain === true && (
              <>
                <div>
                  <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12 }}>Where is the pain?</p>
                  <LocationPicker options={PAIN_LOCATIONS} value={painLocs} onChange={setPainLocs} />
                </div>
                <div>
                  <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12 }}>How severe?</p>
                  <ScaleGrid value={painLevel} onChange={setPainLevel} color="#ef4444" />
                </div>
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const isLast = step === total - 1;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-app)', color: 'var(--text-primary)', fontFamily: 'Inter, sans-serif' }}>
      {/* Top bar */}
      <div style={{ padding: '16px 24px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <button onClick={() => step === 0 ? navigate('/player/forms') : setStep(s => s - 1)}
            style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: 15, fontWeight: 600, padding: 0, fontFamily: 'inherit' }}>
            ← {step === 0 ? 'Back' : 'Previous'}
          </button>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Step {step + 1} of {total}</span>
        </div>
        {/* Progress bar */}
        <div style={{ height: 4, background: 'var(--border)', borderRadius: 2 }}>
          <div style={{
            height: 4,
            width: `${((step + 1) / total) * 100}%`,
            background: '#3b82f6',
            borderRadius: 2,
            transition: 'width 0.3s ease',
          }} />
        </div>
      </div>

      {/* Step content */}
      <div style={{ padding: '28px 24px 140px' }}>
        <h2 style={{ fontSize: 26, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6, lineHeight: 1.2 }}>
          {cfg.title}
        </h2>
        <p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 28 }}>
          {cfg.subtitle}
        </p>
        {renderContent()}
      </div>

      {/* Fixed bottom CTA */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        padding: '16px 24px max(16px, env(safe-area-inset-bottom))',
        background: 'var(--bg-app)',
        borderTop: '1px solid var(--border)',
      }}>
        <button onClick={handleNext} disabled={saving}
          style={{
            width: '100%', height: 52, borderRadius: 12,
            border: 'none',
            background: isLast ? '#10b981' : '#3b82f6',
            color: '#fff',
            fontSize: 16, fontWeight: 700,
            cursor: saving ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
            opacity: saving ? 0.7 : 1,
          }}>
          {saving ? 'Saving…' : isLast ? '✓ Submit' : 'Next →'}
        </button>
      </div>
    </div>
  );
}
